import React from "react";
import { useCheckGeneratorResponse, useIsChanged, useQrShowState } from "@/states/qr.state";
import {
  DEFAULT_LOCATION,
  defaultFileFormat,
  DESKTOP_TYPE_LENGTH,
  TABLET_TYPE_LENGTH,
} from "@/constant";
import { findIndex, forIn } from "lodash";
import { IncomingHttpHeaders } from "http";
import { GetServerSidePropsContext } from "next";
import Intercom, { show, hide } from "@intercom/messenger-js-sdk";
import { useUserUrl } from "@/states/qr_types/url.state";
import {
  useApiError,
  useConfig,
  useEdit,
  useError,
  useFrom,
  useGenerateResult,
} from "@/states/common.state";
import { useUserCall } from "@/states/qr_types/call.state";
import { useUserSms } from "@/states/qr_types/sms.state";
import { useUserText } from "@/states/qr_types/text.state";
import { useUserEmail } from "@/states/qr_types/email.state";
import { useUserRating } from "@/states/qr_types/rating.state";
import { useUserMenu } from "@/states/qr_types/menu.state";
import { useUserPdf } from "@/states/qr_types/pdf.state";
import { useUserForImages, useUserImages } from "@/states/qr_types/image.state";
import { useUserBusinessPage } from "@/states/qr_types/business.state";
import { useUserCoupon } from "@/states/qr_types/coupon.state";
import { useUserSocial, useUSerSocialMedia } from "@/states/qr_types/socialMedia.state";
import { useUserEvent } from "@/states/qr_types/event.state";
import { useUserVideo } from "@/states/qr_types/video.state";
import { useUserLocation } from "@/states/qr_types/location.state";
import { useUserVcard } from "@/states/qr_types/vcard.state";
import { useMp3Preview, useUserMp3 } from "@/states/qr_types/mp3.state";
import { useUserApp } from "@/states/qr_types/app.state";
import { useUserFacebook } from "@/states/qr_types/facebook.state";
import { useSavedDesigns, useUserDesign } from "@/states/design.state";
import { useUserWifi } from "@/states/qr_types/wifi.state";
import { useUserFeedback } from "@/states/qr_types/feedback.state";
import { useUserBitcoin } from "@/states/qr_types/bitcoin.state";
import { landingPageTypes } from "@/constant";
import { useActivateFreePlanSuccess, usePlanActivate } from "@/states/billing.state";
import { IResponseFromQrApi } from "@/types/response.types";
import { cookiePersistStorage } from "./common.helper";
import { QR_TYPES } from "@/constant/qr_type.enum";
import { getCurrentTimeFormatted } from "./time.util";
import { ENDPOINTS } from "@/api/routing";

export function initIntercom(user?: {
  email: string;
  user_id: string;
  created_at: number;
  name: string;
}) {
  let intercom = {
    app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID as string,
    hide_default_launcher: true,
  };

  if (user) intercom = { ...intercom, ...user };

  Intercom(intercom);
}

export function openIntercomWidget(message?: string) {
  if (window.Intercom) {
    window.Intercom(
      "showNewMessage",
      message ??
        "I’m thinking about ending my plan. Can you help me resolve any issues or find a better solution?\n",
    );
  }
}

// Helper function to detect browser
export const getBrowserInfo = (userAgent: string): string => {
  if (userAgent.includes("SamsungBrowser")) return "SamsungBrowser";
  if (userAgent.includes("OPR") || (userAgent.includes("Opera") && !userAgent.includes("Chrome")))
    return "Opera";
  if (userAgent.includes("Edg")) return "Edge";
  if (userAgent.includes("Firefox") && !userAgent.includes("Chrome")) return "Firefox";
  if (
    userAgent.includes("Safari") &&
    !userAgent.includes("Chrome") &&
    !userAgent.includes("Chromium")
  )
    return "Safari";
  if (userAgent.includes("Chrome") || userAgent.includes("Chromium")) return "Chrome";
  return "unknown";
};

export function isValidObject(object: object | null): boolean {
  return object ? Object.keys(object).length > 0 : false;
}

// pdfTitle: test.pdf etc.
export function createShortPdfName(pdfTitle: string, maxLength: number) {
  const [longTitle, extension] = pdfTitle.split(".");

  const TRUNCATE = "...";

  let shortTitle = "";

  for (let i = 0; i <= Math.floor(maxLength / 2); i++) {
    shortTitle += longTitle[i];
  }

  shortTitle += TRUNCATE;

  for (let i = Math.round(maxLength / 2) + TRUNCATE.length; i < maxLength; i++) {
    if (longTitle[i]) {
      shortTitle += longTitle[i];
    }
  }

  shortTitle = shortTitle + "." + extension;

  return shortTitle;
}

export function everyTrue(...res: any[]) {
  return res.every(Boolean);
}

export function hasTrue(...res: any[]) {
  return res.some(Boolean);
}

export function toCapitalize(text: string) {
  return text.charAt(0).toLocaleUpperCase() + text.slice(1);
}

export function runOnClient(func: () => any) {
  if (typeof window !== "undefined") {
    if (window.document.readyState == "loading") {
      window.addEventListener("load", func);
    } else {
      return func();
    }
  }
}

export const initTemporaryDashboardQR = () => {
  const { dashboardQr } = useQrShowState.getState();
  if (dashboardQr && dashboardQr?.redirectCondition) {
    Storage().setStorage({
      key: "temporaryDashboardQr",
      value: JSON.stringify(dashboardQr),
    });
  }
};

export function DEBUGGER(...args: any[]) {
  if (
    process.env.NEXT_PUBLIC_ENVIRONMENT === "development" ||
    process.env.NEXT_PUBLIC_ENVIRONMENT === "pre-production"
  ) {
    console.warn("\x1B[31mDBG - \x1B[34m", ...args);
  }
}

const dynamicImport = (base: string, location: string | null, path?: string, self?: boolean) =>
  import(
    `@/components/page-modules/dashboard/${base}/${location ? location + "/" : ""}${path}${
      self ? "/" + path : ""
    }`
  );

const debounce = (func: any, time: number) => {
  let timer: any;
  const that = this;
  return function (...args: any) {
    const context = that;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      func.apply(context, args);
    }, time);
  };
};

export function getHomePageTypeLimit(isTablet: boolean): number {
  return isTablet ? TABLET_TYPE_LENGTH : DESKTOP_TYPE_LENGTH;
}

const isValidURL = (str: string): boolean => {
  const regexp =
    /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/gm;

  return regexp.test(str);
};

const isValidUrlInPreview = (url: string) => {
  const newValue = !url.includes("://") ? `https://${url}` : url;

  return newValue;
};

type StorageType = {
  key: string;
  value: string;
};

type StorageMethods = {
  setStorage: (storage: StorageType) => void;
  getStorage: (key: string) => string | null;
  getStorageWithParse: (key: string) => string | null;
  removeStorage: (key: string) => void;
  clearStorage: () => void;
};

const Storage = (): StorageMethods => {
  const self: StorageMethods = {
    setStorage: ({ key, value }) => {
      localStorage.setItem(key, value);
    },
    getStorage: (key) => {
      return localStorage.getItem(key);
    },
    getStorageWithParse: (key) => {
      const localStorageData = localStorage.getItem(key) || "";
      return JSON.parse(localStorageData);
    },
    removeStorage: (key) => {
      localStorage.removeItem(key);
    },
    clearStorage: () => {
      localStorage.clear();
    },
  };

  return self;
};

const base64ToImageFile = (url: string) => {
  return fetch(url);
};

const bytesToSize = (bytes: number) => {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (bytes == 0) return "0 Byte";
  //@ts-ignore
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
  //@ts-ignore
  return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
};

const getYoutubeVideoId = (url: string): string => {
  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[7].length == 11 ? match[7] : "";
};

const dayOfWeekDigit = new Date().getDay();

type CopyType = HTMLInputElement | HTMLTextAreaElement;

const copyValue = (element: CopyType | HTMLElement): string => {
  if (element.nodeName === "INPUT" || element.nodeName === "TEXTAREA") {
    const copyText = (element as CopyType).value;
    navigator.clipboard.writeText(copyText);
    return copyText;
  } else {
    const copyText = element.textContent || "";
    navigator.clipboard.writeText(copyText);
    return copyText;
  }
};

function toDataURL(url: string, callback: (params: any) => void) {
  const xhr = new XMLHttpRequest();
  xhr.onload = function () {
    const reader = new FileReader();
    reader.onloadend = function () {
      callback(reader.result);
    };
    reader.readAsDataURL(xhr.response);
  };
  xhr.open("GET", url);
  xhr.responseType = "blob";
  xhr.send();
}

// valid email
const isValidEmail = (email: string) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
};

const errorArr: any = [];

const isChanged = (obj: any, params: any) => {
  const { setIsChanged } = useIsChanged.getState();

  forIn(params, function (value, key) {
    if (obj.payload[key] === undefined) {
      if (obj[key].toUpperCase() !== value.toUpperCase()) {
        if (errorArr.length === 0) {
          errorArr.push({
            name: key,
            value: true,
          });
        } else {
          const index = findIndex(errorArr, (e: any) => e.name === key);

          if (index === -1) {
            errorArr.push({
              name: key,
              value: true,
            });
          } else {
            errorArr[index].value = true;
          }
        }
      } else {
        const index = findIndex(errorArr, (e: any) => e.name === key);
        errorArr[index].value = false;
      }
    } else {
      if (obj.payload[key].toUpperCase() !== value.toUpperCase()) {
        if (errorArr.length === 0) {
          errorArr.push({
            name: key,
            value: true,
          });
        } else {
          const index = findIndex(errorArr, (e: any) => e.name === key);

          if (index === -1) {
            errorArr.push({
              name: key,
              value: true,
            });
          } else {
            errorArr[index].value = true;
          }
        }
      } else {
        const index = findIndex(errorArr, (e: any) => e.name === key);
        errorArr[index].value = false;
      }
    }
  });

  const filtered = errorArr.filter((e: any) => e.value === true);

  if (filtered.length === 0) {
    setIsChanged(false);
  } else {
    setIsChanged(true);
  }
};

const destroyAllState = (val: string, result?: boolean, design?: boolean) => {
  // destroy all state
  const { destroyUserUrl } = useUserUrl.getState();
  const { setPayload } = useConfig.getState();
  const { setDashboardQr } = useQrShowState.getState();
  const { destroyUserVcard } = useUserVcard.getState();
  const { destroyCall } = useUserCall.getState();
  const { destroyUserEmail } = useUserEmail.getState();
  const { destroyUserText } = useUserText.getState();
  const { destroyUserRating } = useUserRating.getState();
  const { destroyUserSocial } = useUserSocial.getState();
  const { destroyUserMenu } = useUserMenu.getState();
  const { destroyUserBusiness } = useUserBusinessPage.getState();
  const { destroySocialMedia } = useUSerSocialMedia.getState();
  const { destroyImages } = useUserImages.getState();
  const { destroyUserFacebook } = useUserFacebook.getState();
  const { destroyUserVideo } = useUserVideo.getState();
  const { destroyGenerateResult } = useGenerateResult.getState();
  const { setError } = useError.getState();
  const { destroySms } = useUserSms.getState();
  const { destroyUserImage } = useUserForImages.getState();
  const { destroyEvent } = useUserEvent.getState();
  const { destroyUserMp3 } = useUserMp3.getState();
  const { destroyUserApp } = useUserApp.getState();
  const { destroyUserCoupon } = useUserCoupon.getState();
  const { destroyLocation } = useUserLocation.getState();
  const { setIsEdit } = useEdit.getState();
  const { destroyUSerWifi } = useUserWifi.getState();
  const { qrShowId } = useQrShowState.getState();
  const { setDeleteKey } = useUserDesign.getState();
  const { destroyUserPdf } = useUserPdf.getState();
  const { destroyApiError } = useApiError.getState();
  const { destroyBitcoinState } = useUserBitcoin.getState();
  const { destroyMp3Preview } = useMp3Preview.getState();
  const { setSelect } = useFrom.getState();
  const { destroyStatus } = useCheckGeneratorResponse.getState();
  const { setIsSavedDesigns } = useSavedDesigns.getState();
  const { destroyUserFeedBack } = useUserFeedback.getState();
  const { destroyActivatedSuccessfully } = useActivateFreePlanSuccess.getState();
  const { destroyStatusPlanActivate } = usePlanActivate.getState();
  const formState = useFrom.getState();

  if (!result && result === undefined) {
    destroyGenerateResult();
  }
  destroyUserImage();
  destroyUserUrl();
  destroyUserVcard();
  destroyCall();
  formState.clean();

  destroyActivatedSuccessfully();
  destroyUserEmail();
  destroyUserText();
  destroyUserRating();
  destroyUserSocial();
  destroyUserMenu();
  destroyUserBusiness();
  destroySocialMedia(qrShowId);
  destroyImages();
  destroyUserFacebook();
  destroyUserVideo();
  setError(null);
  destroySms();
  destroyEvent();
  destroyUserMp3();
  destroyUserApp();
  destroyUserCoupon();
  destroyLocation();
  setPayload(null);
  setIsEdit(false);
  destroyUSerWifi();
  setDeleteKey("markersAndColor");
  destroyUserPdf();
  destroyApiError();
  destroyBitcoinState();
  destroyMp3Preview();
  setSelect(null);
  destroyStatus();
  setDashboardQr({
    validation_errors: null,
  });
  setIsSavedDesigns(false);
  destroyUserFeedBack();
  destroyStatusPlanActivate();
};

export const initChurnkey = () => {
  if (!window?.churnkey || !window?.churnkey.created) {
    window.churnkey = { created: true };
    const a = document.createElement("script");
    a.src = `https://assets.churnkey.co/js/app.js?appId=${process.env.NEXT_PUBLIC_CHURNKEY_APP_ID}`;
    a.async = true;
    const b = document.getElementsByTagName("script")[0];
    b.parentNode?.insertBefore(a, b);
  }
};

const getBrowserName = (isSSr: boolean) => {
  let OS = "Unknown";
  if (!isSSr) {
    if (navigator.userAgent.indexOf("Win") != -1) OS = "Windows";
    if (navigator.userAgent.indexOf("Mac") != -1) OS = "MacOS";
    if (navigator.userAgent.indexOf("X11") != -1) OS = "UNIX";
    if (navigator.userAgent.indexOf("Linux") != -1) OS = "Linux";
  }
  return OS;
};

const deleteKeyFromObject = (obj: any, item: string[]) => {
  item.forEach((_) => {
    delete obj[_];
  });

  return obj;
};

export async function downloadMP3(fileName: string, fileUrl: string) {
  try {
    // temporary comment
    // const res = await fetch(fileUrl);
    // const blob = await res.blob();
    // const url = window.URL.createObjectURL(new Blob([blob]));

    const a = document.createElement("a");
    a.href = fileUrl;
    // a.download = fileName;
    a.click();
    a.remove();
  } catch (e: unknown) {
    DEBUGGER("Download error", e);
  }
}

const download = (
  fileName: string,
  url: string,
  type: string,
  successCallback?: (params: string) => void,
  errorCallback?: () => void,
) => {
  const access_token = cookiePersistStorage.getItem("access_token");
  const { setDownloadQrId } = useQrShowState.getState();

  fetch(url, {
    headers: {
      authorization: "Bearer " + access_token,
    },
  })
    .then((res) => res.blob())
    .then((blob) => {
      const blobURL = window.URL.createObjectURL(blob);
      const fileNameHasType = fileName.includes(type);
      const saveName = fileNameHasType ? fileName : `${fileName}.${type}`;
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = blobURL;
      a.target = "_blank";
      a.download = saveName;
      document.body.appendChild(a);
      a.click();
      if (successCallback) {
        successCallback("download");
      }
      // Clean up
      window.URL.revokeObjectURL(blobURL);
      document.body.removeChild(a);
    })
    .catch((error) => {
      // Handle errors
      if (errorCallback) {
        errorCallback();
      } else {
        DEBUGGER("Error while downloading qr: ", error);
      }
    })
    .finally(() => {
      errorCallback?.();
      setDownloadQrId(null);
    }); // Ensure loading state is reset
};

export const initiateQrDownload = ({
  qrType,
  format,
  downloadQrId,
  qrItem,
  fileSize,
}: {
  qrType: string | null;
  format?: string;
  fileSize?: number | null;
  downloadQrId: number | null;
  qrItem?: any;
}) => {
  const time = getCurrentTimeFormatted();

  const fileName = downloadQrId
    ? `${qrType}_${process.env.NEXT_PUBLIC_SITE_HOST}_${time}.${format || defaultFileFormat}`
    : qrItem.name;

  const baseUrl = downloadQrId
    ? ENDPOINTS.DOWNLOAD_PREVIEW_RESULT(downloadQrId, format || defaultFileFormat)
    : `${qrItem.downloadUrl.split("=")[0]}=${format}`;

  const url = fileSize ? `${baseUrl}&size=${fileSize}` : baseUrl;

  download(fileName, url, format || defaultFileFormat);
};

const getContrastYIQ = (hexcolor: string) => {
  if (hexcolor?.slice(0, 1) === "#") {
    hexcolor = hexcolor.slice(1);
  }
  if (!hexcolor) return;

  const r = parseInt(hexcolor.substr(0, 2), 16);
  const g = parseInt(hexcolor.substr(2, 2), 16);
  const b = parseInt(hexcolor.substr(4, 2), 16);

  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  return yiq >= 128 ? "black" : "white";
};

function isValidHex(color: string): boolean {
  return /^(#(?:[0-9a-f]{2}){2,4}|#[0-9a-f]{3}|(?:rgba?|hsla?)\((?:\d+%?(?:deg|rad|grad|turn)?(?:,|\s)+){2,3}[\s\/]*[\d\.]+%?\))$/i.test(
    color,
  );
}

const setRow = (type: string, qrShowId: null | number, customize: boolean | null): string => {
  if (type === "customize") {
    return "1";
  } else if (type === "setup") {
    if (customize) {
      return "2";
    }
    return "1";
  } else if (type === "design") {
    if (customize) {
      return "3";
    }

    return "2";
  }

  return "";
};

const clearSocialItems = () => {
  const { deleteItem } = useUserVideo.getState();
  // delete key from  videos state
  deleteItem("website");
  deleteItem("youtube");
  deleteItem("instagram");

  const { setDeleteUserSocialMedia } = useUSerSocialMedia.getState();
  // delete key from  social media state
  setDeleteUserSocialMedia("website");
  setDeleteUserSocialMedia("youtube");
  setDeleteUserSocialMedia("instagram");
};

function getObjSize(obj: any) {
  let size = 0;

  for (let key in obj) {
    if (obj.hasOwnProperty(key)) size++;
  }
  return size;
}

function removeObjectWithId(arr: any, id: number) {
  const objWithIdIndex = arr.findIndex((obj: any) => obj.id === id);

  if (objWithIdIndex > -1) {
    arr.splice(objWithIdIndex, 1);
  }

  return arr;
}

function returnErrorAsString(
  type: string,
  className?: string,
  alwaysRenderClassName?: boolean,
): string {
  const { error } = useError.getState();

  return error?.[type]
    ? `validErrorSetup ${className ?? ""}`
    : `${alwaysRenderClassName && className ? className : ""}`;
}

function returnErrorAsBoolean(type: string): boolean {
  const { error } = useError.getState();
  return !!error?.[type];
}

const checkLocationData = (data: any, type?: string | null): string => {
  // checked address data due to state
  const checkedData = (): string[] => {
    const { venue_name, address, street, street_number, city, state, zip, country } = data;
    const hasStreetData = street || street_number || city || state || zip || country;
    const hasAddressData = venue_name || address || state || country;

    if (!type) {
      if (hasStreetData) {
        return [venue_name, street, street_number, city, state, zip, country];
      } else {
        const addressData = [venue_name, address, street, street_number, city, state, zip, country];

        return addressData.every((item) => item === "" || !item)
          ? ["Add your business address", ...addressData.slice(1)]
          : addressData;
      }
    } else {
      if (hasAddressData) {
        return [address, state, country];
      } else {
        return ["Your address", state, country];
      }
    }
  };

  const content: string[] = checkedData().map((item: string) =>
    item ? item.replace(/\s+/g, " ") : "",
  );

  return content.join("\n");
};

const getPreviousDay = (date: Date): Date => {
  const previous = new Date(date.getTime());
  previous.setDate(date.getDate() - 1);

  return previous;
};

function loadingScreenImage<T extends Record<string, any>>(
  data: T,
  qr_type: QR_TYPES,
  type: string = "svg",
): string {
  const imageUrl = `/types/${qr_type?.toLowerCase().replace(/\s+/g, "")}.${type}`;
  if (!!data?.welcome_screen?.url) {
    return `<img src="${data.welcome_screen?.url}" alt="welcome_screen"/>`;
  } else {
    return `<img class="defaultIcon" src="${imageUrl}" alt="defaultIcon"/>`;
  }
}

interface Props {
  initialData: IncomingHttpHeaders;
  concatData?: Record<string, string | number>;
}

export const concatObject = ({ initialData, concatData }: Props) => {
  const JSON_DATA = {
    ...initialData,
    ...(concatData || null),
  };

  return { JSON_DATA };
};

type CustomizeObjectType = {
  setData: (params: IResponseFromQrApi<Record<string, any>>["payload"]) => void;
};

export const customizeStateObject: Record<string, CustomizeObjectType> = {
  vcard: {
    setData: (params) => useUserVcard.getState().setUserVcard(params),
  },
  mp3: {
    setData: (params) => useUserMp3.getState().setUserMp3(params),
  },
  app: {
    setData: (params) => useUserApp.getState().setUserApp(params),
  },
  facebook: {
    setData: (params) => useUserFacebook.getState().setUserFacebook(params),
  },
  pdf: {
    setData: (params) => useUserPdf.getState().setUserPdf(params),
  },
  menu: {
    setData: (params) => useUserMenu.getState().setUserMenu(params),
  },
  location: {
    setData: (params) => useUserLocation.getState().setParams(params),
  },
  video: {
    setData: (params) => useUserVideo.getState().setUserParams(params),
  },
  images: {
    setData: (params) => useUserImages.getState().setUserParams(params),
  },
  businesspage: {
    setData: (params) => useUserBusinessPage.getState().setUserBusiness(params),
  },
  coupon: {
    setData: (params) => useUserCoupon.getState().setUserCoupon(params),
  },
  socialmedia: {
    setData: (params) => useUSerSocialMedia.getState().setUserSocialMedia(params),
  },
  event: {
    setData: (params) => useUserEvent.getState().setUserParams(params),
  },
  text: {
    setData: (params) => useUserText.getState().setParams(params),
  },
};

const renderLocation = (from: string | undefined, children: React.ReactNode, data: any) => {
  if (from) {
    return (
      data.replace(/ /g, "").toLowerCase() !== DEFAULT_LOCATION.replace(/ /g, "").toLowerCase() &&
      children
    );
  } else {
    return children;
  }
};

const getParsedCookie = (ctx: GetServerSidePropsContext) => {
  const { req, resolvedUrl } = ctx;
  const { user_state, access_token } = req.cookies;
  const guestToken = user_state ? JSON.parse(user_state)?.state?.guestToken : null;

  const queryParams = user_state ? JSON.parse(user_state)?.state?.queryParams : null;

  const referer = req.headers.referer;

  return { queryParams, resolvedUrl, referer, guestToken, access_token };
};

function setMyCookie(cname: string, cvalue: string, exdays: number) {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  const expires = "expires=" + d.toUTCString();

  // Build the cookie string
  const cookieString = `${cname}=${encodeURIComponent(cvalue)};${expires};path=/;`;

  // Set the cookie
  document.cookie = cookieString;
}

const clearAllCookies = () => {
  const cookies = document.cookie.split(";");
  cookies.forEach((cookie) => {
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
    // Clear the cookie by setting its expiration date to a past date
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  });
};

//with previous version
const openChatPopup = () => {
  if (typeof window !== "undefined") {
    show();
  }
};

//with previous version
const closeChatPopup = () => {
  if (typeof window !== "undefined") {
    hide();
  }
};

const getTypeName = (type?: string) => {
  let typeName = type || "";

  const selectedType = landingPageTypes.find((item) => item.type === type);
  if (selectedType) typeName = selectedType.dashboardTypeTitle;

  return typeName;
};

function formatFileSizeInSentence(sentence: string) {
  const regex = /(\d+) bytes/;
  const match = sentence.match(regex);

  if (match) {
    const fileSizeInBytes = parseInt(match[1], 10);
    if (!isNaN(fileSizeInBytes)) {
      const fileSizeInMB = fileSizeInBytes / (1024 * 1024);
      const formattedSentence = sentence.replace(match[0], `${fileSizeInMB} MB`);
      return formattedSentence;
    }
  }

  return "Unable to determine file size in the sentence.";
}

function formatFileErrorMessage(error: any, translate?: (key: string) => string) {
  switch (error.code) {
    case "file-invalid-type":
      if (error.message.includes(".pdf")) {
        return (
          translate?.("validation.fileInvalidType") ||
          "Please upload a PDF file with the correct extension (.pdf)"
        );
      }
    // TODO: Add more cases for other file types or error codes as needed.
    case "file-too-large":
      return translate?.("validation.fileTooLarge") || "The file is larger than 30 MB";
    default:
      return translate?.("validation.fileError") || "An error occurred while processing the file.";
  }
}

export function isEven(n: number) {
  return n % 2 === 0;
}

export {
  formatFileErrorMessage,
  formatFileSizeInSentence,
  getTypeName,
  openChatPopup,
  closeChatPopup,
  setMyCookie,
  clearAllCookies,
  dynamicImport,
  clearSocialItems,
  debounce,
  isValidURL,
  isValidUrlInPreview,
  Storage,
  base64ToImageFile,
  bytesToSize,
  getYoutubeVideoId,
  dayOfWeekDigit,
  copyValue,
  toDataURL,
  isValidEmail,
  isChanged,
  destroyAllState,
  getBrowserName,
  deleteKeyFromObject,
  download,
  getContrastYIQ,
  isValidHex,
  setRow,
  getObjSize,
  removeObjectWithId,
  returnErrorAsString,
  returnErrorAsBoolean,
  checkLocationData,
  loadingScreenImage,
  getPreviousDay,
  renderLocation,
  getParsedCookie,
};

export function getIterableValidation(
  key: string,
  errors: Array<Record<string, Array<string>>>,
  includesKey: string,
  checkKey?: string[],
): boolean {
  if (!errors) {
    return false;
  }

  const errorKeys = Object.keys(errors);

  const hasMatchingKey = errorKeys.find((key) => key.includes(includesKey)) === key;

  const hasPrevField = checkKey ? errorKeys.some((err) => checkKey.includes(err)) : false;

  return !hasPrevField && hasMatchingKey;
}

type scrollPosition = "start" | "nearest" | "end" | "center";

export const scrollToLastItem = (
  ref: React.RefObject<HTMLDivElement>,
  block: scrollPosition = "start",
) => {
  const lastChildElement = ref.current?.lastElementChild;
  lastChildElement?.scrollIntoView({ behavior: "smooth", block });
};

export function getLastSegment(path: string): string {
  const segments = path.split("/").filter((segment) => segment !== "");
  return "/" + segments[segments.length - 1];
}

export const limitInputLength = (
  event: React.ChangeEvent<HTMLTextAreaElement>,
  maxLength: number,
) => {
  const { value } = event.target;
  event.target.value = value.slice(0, maxLength);
};
